import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import "../../../assets/css/admin/intro/IntroAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";
import flowerIcon from "../../../assets/img/flower_icon.png";
import PastIntro from "./PastIntro";
import { getStoredAdminInfo } from "../AdminAuth";

function Label({ children }) {
  return (
    <div className="form-label">
      <img src={flowerIcon} alt="" className="label-icon" />
      {children}
    </div>
  );
}

const INTRO_BASE = "/stdcounciladmin/adminhome/intro";

const defaultForm = {
  generation: "",
  name: "",
  slogan: "",
  description: "",
  officeLocation: "",
  kakaoUrl: "",
  instagramUrl: "",
  mainImage: null,
  mainImageName: "",
  mainImgUrl: "",
};

const introTitleMap = {
  intro: "총학생회",
  scitech: "과학기술대학",
  global: "글로벌융합대학",
  future: "미래인재대학",
  pharm: "약학대학",
  artdesign: "아트앤디자인대학",
  club: "동아리연합회",
};

const collegeIdMap = {
  scitech: 1,
  global: 2,
  future: 3,
  pharm: 4,
  artdesign: 5,
  club: 6,
};

const validModes = ["current", "past"];
const validOrgTypes = ["student", "emergency", "acting"];

const orgTypeToCouncilType = {
  student: "STUDENT_COUNCIL",
  emergency: "EMERGENCY_COMMITTEE",
  acting: "ACTING",
};

function IntroAdmin({ title }) {
  const navigate = useNavigate();
  const { type = "intro", mode = "current", orgType = "student" } = useParams();
  const { isStdCouncil } = getStoredAdminInfo();

  const currentMode = validModes.includes(mode) ? mode : "current";
  const currentOrgType = validOrgTypes.includes(orgType) ? orgType : "student";

  const pageTitle = title || introTitleMap[type] || "총학생회";
  const isStudentCouncil = pageTitle === "총학생회";
  const collegeId = collegeIdMap[type];
  const isActing = currentOrgType === "acting";

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [collegeProfileExists, setCollegeProfileExists] = useState(false);

  useEffect(() => {
    if (!isStdCouncil) {
      navigate("/stdcounciladmin/adminhome/intro", {
        replace: true,
      });
    }
  }, [isStdCouncil, navigate]);

  useEffect(() => {
    if (!isStdCouncil) return;

    if (!isStudentCouncil && currentMode === "past") {
      navigate(`${INTRO_BASE}/${type}/current/${currentOrgType}`, {
        replace: true,
      });
    }
  }, [
    isStdCouncil,
    isStudentCouncil,
    currentMode,
    type,
    currentOrgType,
    navigate,
  ]);

  useEffect(() => {
    if (!isStdCouncil) return;

    const fetchCouncilIntro = async () => {
      if (!isStudentCouncil || currentMode !== "current") return;

      try {
        setLoading(true);

        const result = await apiClient("/api/council/intro");
        const data = result?.data;

        if (!data) {
          setForm(defaultForm);
          return;
        }

        setForm({
          ...defaultForm,
          generation: data.termNumber || "",
          name: data.name || "",
          slogan: data.slogan || "",
          description: data.intro || "",
          mainImage: null,
          mainImageName: data.mainImgUrl ? "기존 대표 이미지" : "",
          mainImgUrl: data.mainImgUrl || "",
        });
      } catch (error) {
        console.error("총학생회 소개 조회 오류:", error);
        setForm(defaultForm);
      } finally {
        setLoading(false);
      }
    };

    const fetchCollegeProfile = async () => {
      if (!collegeId || currentMode !== "current") return;

      try {
        setLoading(true);

        const result = await apiClient(`/api/colleges/${collegeId}/profile`);
        const data = result?.data;

        if (!data) {
          setCollegeProfileExists(false);
          setForm(defaultForm);
          return;
        }

        setCollegeProfileExists(true);

        setForm({
          ...defaultForm,
          name: data.name || "",
          slogan: data.slogan || "",
          officeLocation: data.officeLocation || "",
          kakaoUrl: data.kakaoUrl || "",
          instagramUrl: data.instagramUrl || "",
          mainImage: null,
          mainImageName: data.profileImg ? "기존 대표 이미지" : "",
          mainImgUrl: data.profileImg || "",
        });
      } catch (error) {
        console.error("중운위 프로필 조회 오류:", error);
        setCollegeProfileExists(false);
        setForm(defaultForm);
      } finally {
        setLoading(false);
      }
    };

    if (isStudentCouncil) {
      fetchCouncilIntro();
    } else {
      fetchCollegeProfile();
    }
  }, [
    isStdCouncil,
    isStudentCouncil,
    currentMode,
    currentOrgType,
    type,
    collegeId,
  ]);

  const handleModeChange = (nextMode) => {
    if (!isStdCouncil) return;
    if (!isStudentCouncil && nextMode === "past") return;

    navigate(`${INTRO_BASE}/${type}/${nextMode}/${currentOrgType}`);
  };

  const handleOrgTypeChange = (nextOrgType) => {
    if (!isStdCouncil) return;

    const nextMode = !isStudentCouncil ? "current" : currentMode;

    navigate(`${INTRO_BASE}/${type}/${nextMode}/${nextOrgType}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    setForm((prev) => ({
      ...prev,
      [key]: file,
      [`${key}Name`]: file.name,
    }));

    e.target.value = "";
  };

  const saveCouncilIntro = async () => {
    const formData = new FormData();

    formData.append("type", orgTypeToCouncilType[currentOrgType]);
    formData.append("termNumber", isActing ? "" : form.generation);
    formData.append("name", isActing ? "" : form.name);
    formData.append("slogan", currentOrgType === "student" ? form.slogan : "");
    formData.append("intro", isActing ? "" : form.description);

    if (currentOrgType === "student" && form.mainImage) {
      formData.append("mainImg", form.mainImage);
    }

    await apiClient("/api/council/intro", {
      method: "PUT",
      body: formData,
    });
  };

  const saveCollegeProfile = async () => {
    if (!collegeId) {
      throw new Error("중운위 정보를 찾을 수 없습니다.");
    }

    const formData = new FormData();

    formData.append("type", orgTypeToCouncilType[currentOrgType]);
    formData.append("name", isActing ? "" : form.name);
    formData.append("slogan", currentOrgType === "student" ? form.slogan : "");
    formData.append("officeLocation", isActing ? "" : form.officeLocation);
    formData.append("kakaoUrl", isActing ? "" : form.kakaoUrl);
    formData.append("instagramUrl", isActing ? "" : form.instagramUrl);

    if (currentOrgType === "student" && form.mainImage) {
      formData.append("profileImg", form.mainImage);
    }

    const method = collegeProfileExists ? "PUT" : "POST";

    await apiClient(`/api/colleges/${collegeId}/profile`, {
      method,
      body: formData,
    });

    setCollegeProfileExists(true);
  };

  const handleSubmit = async () => {
    if (!isStdCouncil) {
      alert("접근 권한이 없습니다.");
      navigate("/stdcounciladmin/adminhome/intro", {
        replace: true,
      });
      return;
    }

    if (!isActing && !form.name.trim()) {
      alert("이름을 입력해 주세요.");
      return;
    }

    try {
      setLoading(true);

      if (isStudentCouncil) {
        await saveCouncilIntro();
      } else {
        await saveCollegeProfile();
      }

      alert("저장되었습니다.");
    } catch (error) {
      console.error("소개 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const renderCouncilForm = () => {
    if (currentOrgType === "acting") {
      return <div className="empty-state">현재 권한대행 상태입니다.</div>;
    }

    if (currentOrgType === "student") {
      return (
        <>
          <div className="form-row">
            <Label>대수</Label>
            <input
              className="form-input"
              name="generation"
              value={form.generation}
              onChange={handleChange}
              placeholder="대수를 입력해 주세요.('제00대' 형식으로 입력해 주세요)"
            />
          </div>

          <div className="form-row">
            <Label>이름</Label>
            <input
              className="form-input"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="이름을 입력해 주세요."
            />
          </div>

          <div className="form-row">
            <Label>구호</Label>
            <input
              className="form-input"
              name="slogan"
              value={form.slogan}
              onChange={handleChange}
              placeholder="구호를 입력해 주세요."
            />
          </div>

          <div className="form-row">
            <Label>대표 이미지</Label>
            <div className="file-box">
              <input
                className="form-input"
                value={form.mainImageName}
                placeholder="인사말에 들어갈 대표 이미지를 업로드해 주세요.(정사각형 이미지)"
                readOnly
              />

              <label className="file-btn">
                파일 선택
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFileChange(e, "mainImage")}
                />
              </label>
            </div>
          </div>

          <div className="form-row vertical">
            <Label>{pageTitle} 소개</Label>
            <textarea
              className="form-textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="글을 작성해 주세요.(최대 5,000자까지 입력 가능합니다.)"
            />
          </div>
        </>
      );
    }

    return (
      <>
        <div className="form-row">
          <Label>대수</Label>
          <input
            className="form-input"
            name="generation"
            value={form.generation}
            onChange={handleChange}
            placeholder="대수를 입력해 주세요.('제00대' 형식으로 입력해 주세요)"
          />
        </div>

        <div className="form-row">
          <Label>이름</Label>
          <input
            className="form-input"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="이름을 입력해 주세요."
          />
        </div>

        <div className="form-row vertical">
          <Label>비상대책위원회 소개</Label>
          <textarea
            className="form-textarea"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="글을 작성해 주세요.(최대 5,000자까지 입력 가능합니다.)"
          />
        </div>
      </>
    );
  };

  const renderCollegeForm = () => {
    if (currentOrgType === "acting") {
      return <div className="empty-state">현재 권한대행 상태입니다.</div>;
    }

    return (
      <>
        <div className="form-row">
          <Label>이름</Label>
          <input
            className="form-input"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="이름을 입력해 주세요."
          />
        </div>

        {currentOrgType === "student" && (
          <>
            <div className="form-row">
              <Label>구호</Label>
              <input
                className="form-input"
                name="slogan"
                value={form.slogan}
                onChange={handleChange}
                placeholder="구호를 입력해 주세요."
              />
            </div>

            <div className="form-row">
              <Label>대표 이미지</Label>
              <div className="file-box">
                <input
                  className="form-input"
                  value={form.mainImageName}
                  placeholder="대표 이미지를 업로드해 주세요."
                  readOnly
                />

                <label className="file-btn">
                  파일 선택
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleFileChange(e, "mainImage")}
                  />
                </label>
              </div>
            </div>
          </>
        )}

        <div className="form-row">
          <Label>위치</Label>
          <input
            className="form-input"
            name="officeLocation"
            value={form.officeLocation}
            onChange={handleChange}
            placeholder="학생회실 위치를 입력해 주세요."
          />
        </div>

        <div className="form-row">
          <Label>카카오톡</Label>
          <input
            className="form-input"
            name="kakaoUrl"
            value={form.kakaoUrl}
            onChange={handleChange}
            placeholder="카카오톡 URL을 입력해 주세요."
          />
        </div>

        <div className="form-row">
          <Label>인스타그램</Label>
          <input
            className="form-input"
            name="instagramUrl"
            value={form.instagramUrl}
            onChange={handleChange}
            placeholder="인스타그램 URL을 입력해 주세요."
          />
        </div>
      </>
    );
  };

  const renderCurrentForm = () => {
    if (isStudentCouncil) {
      return renderCouncilForm();
    }

    return renderCollegeForm();
  };

  if (!isStdCouncil) {
    return null;
  }

  return (
    <div className="form-card admin-scroll-x">
      <h3 className="form-title">{pageTitle} 소개</h3>

      <div className="toggle-row">
        <div className="top-toggle">
          <button
            type="button"
            className={currentMode === "current" ? "active" : ""}
            onClick={() => handleModeChange("current")}
          >
            현재
          </button>

          {isStudentCouncil && (
            <button
              type="button"
              className={currentMode === "past" ? "active" : ""}
              onClick={() => handleModeChange("past")}
            >
              역대
            </button>
          )}
        </div>

        {currentMode === "current" && (
          <div className="sub-toggle">
            <button
              type="button"
              className={currentOrgType === "student" ? "active" : ""}
              onClick={() => handleOrgTypeChange("student")}
            >
              학생회
            </button>

            <button
              type="button"
              className={currentOrgType === "emergency" ? "active" : ""}
              onClick={() => handleOrgTypeChange("emergency")}
            >
              비상대책위원회
            </button>

            <button
              type="button"
              className={currentOrgType === "acting" ? "active" : ""}
              onClick={() => handleOrgTypeChange("acting")}
            >
              권한대행
            </button>
          </div>
        )}
      </div>

      {currentMode === "current" ? (
        <>
          {renderCurrentForm()}

          <div className="form-footer">
            <button
              className="save-btn"
              onClick={handleSubmit}
              type="button"
              disabled={loading}
            >
              {loading ? "저장 중..." : "저장"}
            </button>
          </div>
        </>
      ) : (
        <PastIntro orgType={currentOrgType} title={pageTitle} />
      )}
    </div>
  );
}

export default IntroAdmin;